import { Stack } from '@mantine/core';
import { IconWrapper } from '@repo/common/components/IconWrapper';
import { Icon, MultiSelect, Text } from '@repo/foundations';
import { useUpdateEffect } from 'ahooks';
import { uniq, uniqBy } from 'lodash-es';
import { useMemo, useState } from 'react';
import type { ISlackChannel, IUser, IUserGroup } from '../../api';
import { useAuthUser } from '../../api';
import { getDisplayName } from '../../utils/userUtils';
import { UserAvatar } from '../UserAvatar';
import { useAnnouncementRecipientsList } from './AnnouncementGlobalRecipients.helpers';
import type { AnnouncementRelatedResourceItem } from './AnnouncementRelatedResource';
import type { AnnouncementFormKeys } from './types';

type RecipientItem = { value: string; group: string } & (
	| {
			group: 'Suggested';
			type: 'user';
			recipient: IUser;
			suffix?: string;
	  }
	| {
			group: 'Suggested';
			type: 'group';
			recipient: IUserGroup;
			suffix?: string;
	  }
	| {
			group: 'Members';
			user: IUser;
	  }
	| {
			group: 'Groups';
			userGroup: IUserGroup;
	  }
	| {
			group: 'Roles';
			role: string;
			userIds: string[];
	  }
	| {
			group: 'Slack Channels';
			channel: ISlackChannel;
	  }
);

const calculateInitialValues = (
	selectedRecipients: string[] | undefined,
	selectedReceiverSlackChannelIds: string[] | undefined,
	data: RecipientItem[]
) => {
	const result: string[] = [];

	if (selectedReceiverSlackChannelIds) {
		result.push(
			...selectedReceiverSlackChannelIds.map(
				(channelId) => `channel_id:${channelId}`
			)
		);
	}

	if (!selectedRecipients || selectedRecipients.length === 0) {
		return result;
	}

	const remainingUserIds = new Set(selectedRecipients);

	// In the backend, we store all recipients as user ids. So we need to recalculate whether we used any roles/groups
	// Step 1: Check if all users in roles (admin, editor, viewer) are selected
	const roles = data.filter((item) => item.group === 'Roles');
	for (const role of roles) {
		const roleUserIds = new Set(role.userIds);
		const selectedRoleUserIds = selectedRecipients.filter(
			(id) => roleUserIds.has(id) && remainingUserIds.has(id)
		);

		if (
			selectedRoleUserIds.length === role.userIds.length &&
			role.userIds.length > 0
		) {
			result.push(role.value);
			selectedRoleUserIds.forEach((id) => remainingUserIds.delete(id));
		}
	}

	// Step 2: Check if all users (from remaining user ids) in user groups are selected
	const groups = data.filter((item) => item.group === 'Groups');
	for (const group of groups) {
		const groupUserIds = new Set(group.userGroup.users);
		const selectedGroupUserIds = selectedRecipients.filter(
			(id) => groupUserIds.has(id) && remainingUserIds.has(id)
		);

		if (
			selectedGroupUserIds.length === group.userGroup.users.length &&
			group.userGroup.users.length > 0
		) {
			result.push(group.value);
			selectedGroupUserIds.forEach((id) => remainingUserIds.delete(id));
		}
	}

	// Step 3: Add remaining individual users
	remainingUserIds.forEach((userId) => {
		result.push(`user_id:${userId}`);
	});

	return result;
};

export function AnnouncementEntityRecipients({
	onChange,
	selectedData,
	selectedRecipients,
	selectedReceiverSlackChannelIds,
}: {
	onChange: (key: AnnouncementFormKeys) => (value: string | string[]) => void;
	selectedData: AnnouncementRelatedResourceItem[];
	selectedRecipients?: string[];
	selectedReceiverSlackChannelIds?: string[];
}) {
	const data = useAnnouncementRecipientsList(selectedData);

	const initialValues = useMemo(
		() =>
			calculateInitialValues(
				selectedRecipients,
				selectedReceiverSlackChannelIds,
				data
			),
		[selectedRecipients, data, selectedReceiverSlackChannelIds]
	);
	const [value, setValue] = useState<string[]>(initialValues);
	const { user } = useAuthUser();

	useUpdateEffect(() => {
		const idsToValues = data.filter((d) => value.includes(d.value));
		const groupToUserIds = idsToValues
			.filter((d) => d.group === 'Groups')
			.map((d) => d.userGroup.users)
			.flat();
		const userIds = idsToValues
			.filter((d) => d.group === 'Members')
			.map((d) => d.user.id);
		const suggestedUserIds = idsToValues
			.filter((d) => d.group === 'Suggested')
			.map((d) => (d.type === 'user' ? d.recipient.id : d.recipient.users))
			.flat();
		const roleToUserIds = idsToValues
			.filter((d) => d.group === 'Roles')
			.map((d) => d.userIds)
			.flat();
		const channelsToIds = idsToValues
			.filter((d) => d.group === 'Slack Channels')
			.map((d) => d.channel.id);

		onChange('receiver_slack_channel_ids')(uniq(channelsToIds));
		onChange('recipients')(
			uniq([
				...userIds,
				...groupToUserIds,
				...roleToUserIds,
				...suggestedUserIds,
				user!.id,
			])
		);
	}, [value, selectedData]);

	const displayIcon = (item: RecipientItem) => {
		if (item.group === 'Members') {
			return <UserAvatar user={item.user} size="xxs" />;
		} else if (item.group === 'Groups') {
			return (
				<IconWrapper>
					<Text size="sm">{item.userGroup.icon}</Text>
				</IconWrapper>
			);
		} else if (item.group === 'Slack Channels') {
			return (
				<IconWrapper>
					<Icon name="hash" />
				</IconWrapper>
			);
		} else if (item.group === 'Suggested' && item.type === 'user') {
			return <UserAvatar user={item.recipient} size="xxs" />;
		} else if (item.group === 'Suggested' && item.type === 'group') {
			return (
				<IconWrapper>
					<Text size="sm">{item.recipient.icon}</Text>
				</IconWrapper>
			);
		}
		return (
			<IconWrapper>
				<Icon name="user" />
			</IconWrapper>
		);
	};

	const displayLabel = (item: RecipientItem) => {
		if (item.group === 'Members') {
			return getDisplayName(item.user);
		} else if (item.group === 'Groups') {
			return item.userGroup.name;
		} else if (item.group === 'Slack Channels') {
			return item.channel.name;
		} else if (item.group === 'Suggested' && item.type === 'user') {
			return getDisplayName(item.recipient);
		} else if (item.group === 'Suggested' && item.type === 'group') {
			return item.recipient.name;
		}
		return item.role;
	};

	return (
		<Stack>
			<MultiSelect
				clearable
				renderIcon={(item) => displayIcon(item)}
				renderLabel={(item) => displayLabel(item)}
				size="md"
				sx={{
					'.mantine-MultiSelect-input': {
						maxHeight: 100,
						overflowY: 'auto',
					},
				}}
				value={value}
				setValue={setValue}
				searchable
				label="Recipients"
				// We can use uniqBy here because it keeps the first occurrence of the duplicates.
				// We only call it at this level because we need an unfilled array to be able to show all applicable checkboxes (below).
				data={uniqBy(data, 'value')}
			/>
		</Stack>
	);
}
